import React, { useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';

interface RowConfigType<B> {
  onClick?: () => void;
  className?: string;
  customCellContentTemplate?: (col: TableConfigType<B>, row: B | undefined) => JSX.Element;
}

interface ColumnConfigType<B> {
  className?: string;
  customCellContentTemplate?: (col: TableConfigType<B>, value: string) => JSX.Element;
}

export interface TableConfigType<B> {
  title: string;
  dataRef: string;
  value?: string;
  className?: string;
  rowConfig?: RowConfigType<B>;
  columnConfig?: ColumnConfigType<B>;
}

interface IHorizontalScrollTable<
  T extends { data?: B[] },
  B extends Record<string, string | number | null | undefined>,
> {
  isLoading?: boolean;
  dataSetIn: T[];
  tableConfigIn: TableConfigType<B>[];
  fetchMore?: () => Promise<T[]>;
  from?: number;
  pageSize?: number;
}

const useStyles = makeStyles(() => ({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRight: `1px solid #D3D3D3`,
  },
}));

const HorizontalScrollTable = <T extends { data?: B[] }, B extends Record<string, string | number | null | undefined>>({
  dataSetIn,
  tableConfigIn,
  fetchMore,
  isLoading,
}: IHorizontalScrollTable<T, B>) => {
  const classes = useStyles();

  const { lastItemRef, isLoading: isLoadingMore, results } = useInfiniteScroll({ rows: dataSetIn, fetchMore });

  const tableConfig = useMemo(() => {
    return { columns: tableConfigIn, dataRow: results };
  }, [results, tableConfigIn]);

  return (
    <div>
      <table className={`tw-table-auto tw-min-w-[800px] tw-text-sm tw-text-left tw-text-gray-500 ${classes.table}`}>
        <thead className="tw-text-sm tw-text-black">
          <tr>
            {tableConfig.columns.map((col, index) => {
              if (col?.columnConfig?.customCellContentTemplate) {
                return (
                  <th key={`col-${index}`} scope="col" className={col.columnConfig?.className}>
                    {col?.columnConfig?.customCellContentTemplate(col, col.title)}
                  </th>
                );
              }

              return (
                <th key={`col-${index}`} scope="col" className={col.columnConfig?.className}>
                  {col.title}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? new Array(20).fill(1).map((row, rIndex) => {
                return (
                  <tr key={`row-${rIndex}`}>
                    <th key={`cell-${rIndex}`} colSpan={10}>
                      <div className="tw-min-h-[45px] tw-p-0 tw-px-2 tw-bg-gray-300 tw-animate-pulse" />
                    </th>
                  </tr>
                );
              })
            : tableConfig.dataRow.map((dataRow, rIndex) => {
                let lastElemRefOption = {};
                if (tableConfig.dataRow.length === rIndex + 1) {
                  lastElemRefOption = { ref: lastItemRef };
                }
                return (
                  <tr {...lastElemRefOption} key={`row-${rIndex}`}>
                    {tableConfig.columns.map((col, cellIndex) => {
                      const row = col?.value
                        ? (dataRow.data || []).find((x) => `${x?.[col.dataRef]}` === String(col?.value))
                        : dataRow.data?.[0];

                      if (col?.rowConfig?.customCellContentTemplate) {
                        return (
                          <th key={`cell-${cellIndex}`} scope="row" className={col.rowConfig?.className}>
                            {col?.rowConfig?.customCellContentTemplate(col, row)}
                          </th>
                        );
                      }
                      return (
                        <th key={`cell-${cellIndex}`} scope="row" className={col.rowConfig?.className}>
                          {row?.[col.dataRef] ?? (dataRow as unknown as Record<string, string>)[col.dataRef]}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {isLoadingMore && <p className="tw-text-black">Is Loading...</p>}
    </div>
  );
};

export default HorizontalScrollTable;
